/**
 * useCreditBalance — live personal credit balance for the header pill.
 *
 * Server state via TanStack Query, keyed `["credit-balance"]`. The value is
 * kept fresh by two mechanisms:
 *   1. The `activity.credit-balance-changed` WebSocket event, consumed in
 *      useUnifiedEventConsumer, invalidates this query so it refetches. The
 *      balance is not carried on the wire — the event is a signal only.
 *   2. A 60s `refetchInterval` backstop guarantees eventual correctness even
 *      if an event is missed (dropped socket, reconnect gap).
 *
 * Query-hook shape mirrors useMySubscription in useTeamBilling.ts.
 */
import { useQuery } from "@tanstack/react-query";
import { getCreditBalance, type CreditBalance } from "../services/billingApi";
import { getResponseStatus } from "../utils/errorUtils";

export const CREDIT_BALANCE_QUERY_KEY = ["credit-balance"] as const;

const STALE_MS = 60_000;
const REFETCH_MS = 60_000;

export function useCreditBalance() {
  const query = useQuery<CreditBalance>({
    queryKey: CREDIT_BALANCE_QUERY_KEY,
    queryFn: getCreditBalance,
    staleTime: STALE_MS,
    refetchInterval: REFETCH_MS,
    retry: (failureCount, error: unknown) => {
      const status = getResponseStatus(error);
      if (status === 401 || status === 403) return false;
      return failureCount < 3;
    },
  });

  return {
    balance: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
  };
}
